/** /route_sets/ — cross-tenant line-set catalog (Vehicle_Route_Set).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). A line set is a nested tree: set → routes[] (ordered by
 * `sequence`) → each route's ordered stop-links + breaks. `loop_duration` is
 * integer SECONDS on the wire. Writes carry `expected_updated_ts` (409
 * `route_set_modified` on drift).
 *
 * WRITE MODEL (link-wrapped, full-array upsert):
 *  - routes[] matched by `route.id`, reordered by `sequence`; omitted routes are
 *    unlinked (the route row survives).
 *  - stops[] matched by `stop.id`, reordered by `sequence`; omitted stops are
 *    unlinked (and orphan-deleted if unused elsewhere).
 *  - breaks[] matched by `id`; omitted breaks are deleted.
 * On the POST-create path the funnel creates stops FRESH (needs `stop.name`);
 * existing stops are reused only on PATCH via `stop:{id}`. So the console flow
 * is: create a name-only shell via POST, then PATCH to attach routes + existing
 * stops (see LineSetCreateDialog).
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

// ---------------------------------------------------------------------------
// Read shapes (deep nested)
// ---------------------------------------------------------------------------

/** A stop's placement on a route (the through row). `stop` is a brief ref into
 *  the shared `/route_stops/` catalog. */
export interface RouteStopLink {
  /** The through-row id (informational; writes upsert by `stop.id`). */
  id: number;
  sequence: number;
  loop_offset: number;
  first_departure: string | null;
  last_full_loop: string | null;
  last_departure: string | null;
  stop: {
    id: number;
    name: string;
    label: string | null;
    latitude: number | null;
    longitude: number | null;
  };
}

export interface RouteBreak {
  id?: number;
  start_time: string | null;
  end_time: string | null;
}

export interface Route {
  id: number;
  name: string;
  description: string | null;
  enabled: boolean;
  /** Hex, e.g. #00aaff. */
  color: string;
  start_time: string | null;
  end_time: string | null;
  /** Integer seconds (nullable). */
  loop_duration: number | null;
  frequency: string | null;
  warning: string | null;
  region: NamedRef | null;
  country: NamedRef | null;
  tags: NamedRef[];
  breaks: RouteBreak[];
  stops: RouteStopLink[];
}

/** A route's placement in a set (ordered by `sequence`). */
export interface RouteSetRouteLink {
  /** The through-row id (informational; writes upsert by `route.id`). */
  id: number;
  sequence: number;
  route: Route;
}

export interface RouteSetListItem {
  id: number;
  tenant: TenantRef;
  name: string;
  route_count: number;
  updated_ts: string | null;
}

export interface RouteSetDetail extends RouteSetListItem {
  atag: NamedRef[];
  tags: NamedRef[];
  routes: RouteSetRouteLink[];
}

// ---------------------------------------------------------------------------
// Write shapes (link-wrapped)
// ---------------------------------------------------------------------------

export interface RouteStopLinkWrite {
  sequence: number;
  loop_offset: number;
  first_departure: string | null;
  last_full_loop: string | null;
  last_departure: string | null;
  /** Existing bus stop reused by id (PATCH path). */
  stop: { id: number };
}

export interface RouteWrite {
  /** Updates an existing route (must belong to the set's tenant); omit to create. */
  id?: number;
  name: string;
  description?: string | null;
  enabled?: boolean;
  color?: string;
  start_time?: string | null;
  end_time?: string | null;
  /** Integer seconds. */
  loop_duration?: number | null;
  frequency?: string | null;
  warning?: string | null;
  region_id?: number | null;
  country_id?: number | null;
  tags?: { id: number }[];
  breaks?: RouteBreak[];
  stops?: RouteStopLinkWrite[];
}

export interface RouteSetRouteLinkWrite {
  /** Order in the set; defaults to array position. */
  sequence: number;
  route: RouteWrite;
}

/** POST-create body: `tour_operator` + `name` required. On create the console
 *  sends a name-only shell (routes attach on the follow-up PATCH). */
export interface RouteSetCreateInput {
  tour_operator: number;
  name: string;
}

/** PATCH body — full-array upsert of routes/stops/breaks + reorder. */
export interface RouteSetWriteJson {
  name?: string;
  atag?: { id: number }[];
  tags?: { id: number }[];
  routes?: RouteSetRouteLinkWrite[];
  expected_updated_ts?: string | null;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<RouteSetListItem>> {
  return apiGet<PaginatedResponse<RouteSetListItem>>(`/route_sets/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<RouteSetDetail> {
  return apiGet<RouteSetDetail>(`/route_sets/${id}/`);
}

export function create(body: RouteSetCreateInput): Promise<RouteSetDetail> {
  return apiPost<RouteSetDetail>('/route_sets/', body);
}

export function update(id: number, body: RouteSetWriteJson): Promise<RouteSetDetail> {
  return apiPatch<RouteSetDetail>(`/route_sets/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/route_sets/${id}/`);
}
